import React from 'react';
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { TrendingUp } from 'lucide-react';

interface ForecastData {
  year: string;
  confirmed: number;
  projected: number;
  weighted: number;
  minimum: number;
}

interface ForecastChartProps {
  data: ForecastData[];
  timeframe: {
    start: string;
    end: string;
    ticks: string[];
  };
  formatBudget: (value: number) => string;
}

export function ForecastChart({ data, timeframe, formatBudget }: ForecastChartProps) {
  const currentYear = new Date().getFullYear().toString();
  const previousYear = (parseInt(currentYear) - 1).toString();

  const filledData = timeframe.ticks.map(year => {
    const existingData = data.find(d => d.year === year);
    const confirmedValue = existingData?.confirmed || 0;
    const projectedValue = existingData?.projected || 0;
    const weightedValue = existingData?.weighted || 0;
    const minimumValue = existingData?.minimum || 0;

    // For years before current year, use confirmed values for all lines
    if (year < currentYear) {
      return {
        year,
        confirmed: confirmedValue,
        projected: confirmedValue,
        weighted: confirmedValue,
        minimum: confirmedValue
      };
    }

    // For current and future years, use projected values
    return {
      year,
      confirmed: confirmedValue,
      projected: projectedValue,
      weighted: weightedValue,
      minimum: minimumValue
    };
  });

  // Calculate the clip path positions with 10% offset
  const previousYearIndex = timeframe.ticks.indexOf(previousYear);
  const projectedStartPosition = ((previousYearIndex / timeframe.ticks.length) * 100) + 10;
  const projectedWidth = ((timeframe.ticks.length - previousYearIndex) / timeframe.ticks.length) * 100 - 10;

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
      <div className="flex items-center mb-4">
        <TrendingUp className="w-5 h-5 text-indigo-600 mr-2" />
        <h2 className="text-xl font-semibold text-gray-800">Revenue Forecast</h2>
      </div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={filledData}>
            <defs>
              {/* Clip path for confirmed values (show only up to current year) */}
              <clipPath id="clip-confirmed">
                <rect
                  x="0%"
                  y="0%"
                  width={`${(timeframe.ticks.indexOf(currentYear) / timeframe.ticks.length) * 100}%`}
                  height="100%"
                />
              </clipPath>

              {/* Clip path for projected values (show from previous year onwards + 10% offset) */}
              <clipPath id="clip-projected">
                <rect
                  x={`${projectedStartPosition}%`}
                  y="0%"
                  width={`${projectedWidth}%`}
                  height="100%"
                />
              </clipPath>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis
              dataKey="year"
              type="category"
              ticks={timeframe.ticks}
              stroke="#9CA3AF"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tickFormatter={(value) => formatBudget(value)}
              stroke="#9CA3AF"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: 'none',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              formatter={(value: any) => formatBudget(value)}
              labelFormatter={(year: string) => `Year ${year}`}
            />
            {/* Render minimum line first (in the back) */}
            <Area
              type="monotone"
              dataKey="minimum"
              stroke="#EF4444"
              fill="none"
              strokeWidth={2}
              strokeDasharray="4 4"
              name="Minimum"
              clipPath="url(#clip-projected)"
            />
            {/* Render maximum line */}
            <Area
              type="monotone"
              dataKey="projected"
              stroke="#10B981"
              fill="none"
              strokeWidth={2}
              strokeDasharray="4 4"
              name="Maximum"
              clipPath="url(#clip-projected)"
            />
            {/* Render projected line */}
            <Area
              type="monotone"
              dataKey="weighted"
              stroke="#F59E0B"
              fill="none"
              strokeWidth={2}
              strokeDasharray="4 4"
              name="Projected"
              clipPath="url(#clip-projected)"
            />
            {/* Render confirmed line last (on top) */}
            <Area
              type="monotone"
              dataKey="confirmed"
              stroke="#3B82F6"
              fill="none"
              strokeWidth={2}
              name="Confirmed"
              clipPath="url(#clip-confirmed)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}